/**
 * Voxly — generated phone ringtone.
 *
 * Plays the classic two-tone "ring … ring" pattern using the Web Audio API
 * so we don't need any audio file in the bundle. Designed to be started
 * exactly once per call and stopped when the remote participant joins (or
 * the user hangs up).
 *
 * Usage:
 *   const ring = createRingtone();
 *   ring.start();   // user-gesture-initiated, e.g. inside an onClick callback
 *   ring.stop();
 */
export function createRingtone() {
  let ctx = null;
  let intervalId = null;
  let stopped = false;

  const playRing = () => {
    if (stopped || !ctx) return;
    // Each "ring" is a 1-second double-pulse at 440 + 480 Hz — close to the
    // standard North-American + European ringback tones.
    const now = ctx.currentTime;
    const pulseLength = 0.7;

    const makePulse = (start) => {
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      osc1.frequency.value = 440;
      osc2.frequency.value = 480;
      osc1.type = "sine";
      osc2.type = "sine";

      const gain = ctx.createGain();
      // Gentle attack + release so the ring isn't a harsh click.
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.18, start + 0.05);
      gain.gain.setValueAtTime(0.18, start + pulseLength - 0.05);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + pulseLength);

      osc1.connect(gain);
      osc2.connect(gain);
      gain.connect(ctx.destination);
      osc1.start(start);
      osc2.start(start);
      osc1.stop(start + pulseLength);
      osc2.stop(start + pulseLength);
    };

    // Two pulses 0.2 s apart, then 3 s of silence before the next ring.
    makePulse(now);
    makePulse(now + pulseLength + 0.2);
  };

  return {
    start() {
      if (ctx) return; // already started
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        ctx = new AudioContext();
        // Some browsers create the context suspended — best-effort resume.
        if (ctx.state === "suspended") ctx.resume().catch(() => {});
        playRing();
        intervalId = setInterval(playRing, 4000); // 4s = 1.6s ring + ~2.4s pause
      } catch {
        // Audio context creation failed (e.g. autoplay block). Silent no-op.
      }
    },
    stop() {
      stopped = true;
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
      if (ctx) {
        try { ctx.close(); } catch {}
        ctx = null;
      }
    },
  };
}
